import { z } from 'zod';
import { sortSearchParam } from '@/components/data-table/tableSort';
import type { PersonRow } from './peopleApi';
import { hasLeft, todayIso } from './peopleDates';

export type WorkingValue = 'working' | 'left' | 'locked';

/** Mỗi mảng rỗng = không lọc theo tiêu chí đó; nhiều giá trị trong một tiêu chí là "hoặc", các tiêu chí với nhau là "và". */
export interface PeopleFilter {
  q: string;
  titles: string[];
  contracts: string[];
  working: WorkingValue[];
}

export const EMPTY_PEOPLE_FILTER: PeopleFilter = { q: '', titles: [], contracts: [], working: [] };

/** Bỏ dấu + chữ thường để "mai" khớp "Mai", "hoang" khớp "Hoàng". */
const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd');

export const isFiltering = (f: PeopleFilter) => f.q.trim() !== '' || f.titles.length > 0 || f.contracts.length > 0 || f.working.length > 0;

/** Đang làm = còn đăng nhập được và chưa qua ngày nghỉ; Đã nghỉ = qua ngày nghỉ; Đã khoá = tài khoản bị khoá. */
export function matchesWorking(p: PersonRow, w: WorkingValue, today: string): boolean {
  if (w === 'left') return hasLeft(p, today);
  if (w === 'locked') return !p.active;
  return p.active && !hasLeft(p, today);
}

export function filterPeople(rows: PersonRow[], f: PeopleFilter, today: string = todayIso()): PersonRow[] {
  const q = fold(f.q.trim());
  return rows.filter((p) => {
    if (f.titles.length && !(p.title && f.titles.includes(p.title))) return false;
    if (f.contracts.length && !(p.contractType && f.contracts.includes(p.contractType))) return false;
    if (f.working.length && !f.working.some((w) => matchesWorking(p, w, today))) return false;
    if (!q) return true;
    return [p.name, p.shortName, p.code, p.email].some((v) => v && fold(v).includes(q));
  });
}

/** Giá trị có thật trong dữ liệu, kèm số người — không dựng danh mục chức vụ / loại HĐ ngoài những gì đang có người giữ. */
export function filterOptions(rows: PersonRow[]): { titles: { value: string; count: number }[]; contracts: { value: string; count: number }[] } {
  const tally = (xs: (string | null)[]) => {
    const m = new Map<string, number>();
    xs.forEach((x) => x && m.set(x, (m.get(x) ?? 0) + 1));
    return [...m].map(([value, count]) => ({ value, count })).sort((a, b) => a.value.localeCompare(b.value, 'vi'));
  };
  return { titles: tally(rows.map((r) => r.title)), contracts: tally(rows.map((r) => r.contractType)) };
}

// Bộ lọc nằm trong URL (rule 10): `?q=&t=KTS&c=thuc_tap&w=working`. Mặc định (rỗng) vắng khỏi URL; giá trị lạ coi như không có.
const strList = (v: unknown): string[] | undefined => {
  const xs = (Array.isArray(v) ? v : typeof v === 'string' ? [v] : []).filter((x): x is string => typeof x === 'string' && x !== '');
  return xs.length ? xs : undefined;
};
export const peopleSearchSchema = z.object({
  q: z.unknown().transform((v) => (typeof v === 'string' && v.trim() !== '' ? v : undefined)).optional(),
  t: z.unknown().transform(strList).optional(),
  c: z.unknown().transform(strList).optional(),
  s: sortSearchParam,
  /** Mã NV cần mở hộp đổi vai trò (link từ Chờ duyệt). */
  vt: z.unknown().transform((v) => (typeof v === 'string' && v.trim() !== '' ? v : undefined)).optional(),
  w: z.unknown().transform((v) => strList(v)?.filter((x): x is WorkingValue => x === 'working' || x === 'left' || x === 'locked')).transform((v) => (v?.length ? v : undefined)).optional(),
});
export type PeopleSearch = z.infer<typeof peopleSearchSchema>;

export const readPeopleSearch = (s: PeopleSearch): PeopleFilter => ({ q: s.q ?? '', titles: s.t ?? [], contracts: s.c ?? [], working: s.w ?? [] });
export const writePeopleSearch = (f: PeopleFilter): PeopleSearch => ({
  q: f.q.trim() === '' ? undefined : f.q,
  t: f.titles.length ? f.titles : undefined,
  c: f.contracts.length ? f.contracts : undefined,
  w: f.working.length ? f.working : undefined,
});
